import React, {
  createContext,
  useMemo,
  useState,
  useEffect,
  useCallback,
  useRef,
} from 'react';
import FeatureToggleProvider from './FeatureToggleProvider';
import { ProfileInformations } from '../templates/profile/interface';
import { getSSOUrls } from '../common/utils/ssoEndPoints';
import { getDecryptedValue, setEncryptedValue } from '../common/utils/LocalStorageUtils';

const USER_TELECOM_CACHE_KEY = 'userTelecom';

export const GlobalContext = createContext({} as any);
const GlobalProvider = ({ children }: any) => {
  const [userEncodedData, setUserEncodedData] = useState([]);
  const [getFeature, setFeature] = useState({});
  const [zipValue, setZipValue] = useState('');
  const [addressValue, setAddressValue] = useState('');
  const [proxyDataValue, setProxyDataValue] = useState('');
  const [proxyData, setProxyData] = useState([]);
  const [age, setAge] = useState('');
  const [enableView, setEnableView] = useState(false);
  const [proxySelected, setProxySelected] = useState([]);
  const [proxyMemberId, setProxyMemberId] = useState('');
  const [proxyDropDown, setProxyDropDown] = useState(false);
  const [enableDelete, setEnableDelete] = useState(false);
  const [updateProxy, setUpdateProxy] = useState(false);
  const [showContent, setShowContent] = useState(false);
  const [loginMID, setLoginMID] = useState('');
  const [loginProxyId, setLoginProxyId] = useState('');
  const [loginMemberId, setLoginMemberId] = useState('');
  const [getAuthToken, setAuthToken] = useState('');
  const [proxyUniqueId, setProxyUniqueId] = useState('');
  const [profileState, setProfileState] = useState<ProfileInformations>({});
  const [xauthToken, setXauthToken] = useState('');
  const [xsentaraTokenID, setXsentaraTokenID] = useState('');
  const [isDesignatedRepresentative, setIsDesignatedRepresentative] =
    useState(false);
  const [designatedDropdownLink, setDesignatedDropdownLink] = useState(false);
  const [viewModal, setViewModal] = useState(false);
  const [confirmProxy, setConfirmProxy] = useState([]);
  const [memberProxyData, setMemberProxyData] = useState([]);
  const [proxyUpdate, setProxyUpdate] = useState(false);
  const [isGuestSwitch, setIsGuestSwitch] = useState(false);
  const [isBacktoDashBoard, setIsBacktoDashBoard] = useState(false);
  const [primaryUser, setPrimaryUser] = useState('');
  const [selectedIdCardMemberId, setSelectedIdCardMemberId] =
    useState<string>('');
  const [planName, setPlanName] = useState<string>('');
  const [planWellness, setPlanWellness] = useState<string>('');
  const [serviceNumber, setServiceNumber] = useState<string>('');
  const [isMemberTabDefault, setIsMemberTabDefault] = useState<boolean>(false);
  const [ssoLink, setSSOLink] = useState<string>('');
  const [isDesignatedTabShow, setIsDesignatedTabShow] =
    useState<boolean>(false);
  const [onEmailUpdate, setOnEmailUpdate] = useState<boolean>(false);
  const [ssoEndPoints, setSsoEndPoints] = useState<any>(null);
  const [logging, setLogging] = useState<any>(null);
  const [monitoring, setMonitoring] = useState<any>(null);
  const [healthEquityHSA, setHealthEquityHSA] = useState<boolean>(false);
  const [accountType, setAccountType] = useState('');
  const [userTelecom, setUserTelecom] = useState<any[]>([]);
  const cachedTelecom = useRef(false);

  useEffect(() => {
    const loadUserTelecom = async () => {
      try {
        const cached = await getDecryptedValue(USER_TELECOM_CACHE_KEY);

        if (cached) {
          const telecom =
            typeof cached === 'string' ? JSON.parse(cached) : cached;

          if (Array.isArray(telecom)) {
            setUserTelecom(telecom);
          }
        }
      } catch (error) {
        console.error('Failed to load telecom cache:', error);
      } finally {
        cachedTelecom.current = true;
      }
    };

    void loadUserTelecom();
  }, []);

  useEffect(() => {
    if (!cachedTelecom.current) return;

    void setEncryptedValue(
      USER_TELECOM_CACHE_KEY,
      JSON.stringify(userTelecom)
    ).catch((error) => {
      console.error('Failed to persist telecom cache:', error);
    });
  }, [userTelecom]);

  // Function to fetch SSO URLs
  const refetchSSOUrls = useCallback(async () => {
    try {
      const urls = await getSSOUrls();
      setSsoEndPoints(urls);
    } catch (error) {
      console.error('Error fetching SSO URLs:', error);
    }
  }, []);

  // Fetch SSO URLs on mount
  useEffect(() => {
    refetchSSOUrls();
  }, [refetchSSOUrls]);

  // Listen for i18next's built-in languageChanged event
  useEffect(() => {
    let cleanup: (() => void) | undefined;

    // Dynamically import i18n to avoid circular dependency
    import('../i18n')
      .then((i18nModule) => {
        const i18n = i18nModule.default;

        const handleLanguageChange = () => {
          refetchSSOUrls();
        };

        i18n.on('languageChanged', handleLanguageChange);

        cleanup = () => {
          i18n.off('languageChanged', handleLanguageChange);
        };
      })
      .catch((error) => {
        console.error('Failed to load i18n module:', error);
      });

    return () => {
      cleanup?.();
    };
  }, [refetchSSOUrls]);

  //This Function will be called on signout to clear all context
  const clearContextValues = () => {
    setUserEncodedData([]);
    setFeature({});
    setZipValue('');
    setAddressValue('');
    setProxyDataValue('');
    setProxyData([]);
    setAge('');
    setEnableView(false);
    setProxySelected([]);
    setProxyDropDown(false);
    setEnableDelete(false);
    setUpdateProxy(false);
    setShowContent(false);
    setLoginMID('');
    setLoginProxyId('');
    setLoginMemberId('');
    setIsDesignatedRepresentative(false);
    setDesignatedDropdownLink(false);
    setViewModal(false);
    setConfirmProxy([]);
    setMemberProxyData([]);
    setProxyUpdate(false);
    setIsGuestSwitch(false);
    setIsBacktoDashBoard(false);
    setPrimaryUser('');
    setSelectedIdCardMemberId('');
    setPlanName('');
    setPlanWellness('');
    setServiceNumber('');
    setIsMemberTabDefault(false);
    setSSOLink('');
    setIsDesignatedTabShow(false);
    setProxyUniqueId('');
    setOnEmailUpdate(false);
    setProfileState({});
    setLogging(null);
    setMonitoring(null);
    setHealthEquityHSA(false);
    setAccountType('');
    setUserTelecom([]);
  };

  const globalContextVal = useMemo(
    () => ({
      userEncodedData,
      setUserEncodedData,
      getFeature,
      setFeature,
      setZipValue,
      zipValue,
      setAddressValue,
      addressValue,
      setProxyDataValue,
      proxyDataValue,
      loginMemberId,
      setLoginMemberId,
      setProxyData,
      proxyData,
      age,
      setAge,
      proxySelected,
      setProxySelected,
      setEnableView,
      enableView,
      proxyDropDown,
      setProxyDropDown,
      enableDelete,
      setEnableDelete,
      updateProxy,
      setUpdateProxy,
      setShowContent,
      showContent,
      isDesignatedRepresentative,
      setIsDesignatedRepresentative,
      designatedDropdownLink,
      setDesignatedDropdownLink,
      memberProxyData,
      setMemberProxyData,
      viewModal,
      setViewModal,
      setProxyUpdate,
      proxyUpdate,
      setConfirmProxy,
      confirmProxy,
      isGuestSwitch,
      setIsGuestSwitch,
      isBacktoDashBoard,
      setIsBacktoDashBoard,
      primaryUser,
      setPrimaryUser,
      selectedIdCardMemberId,
      setSelectedIdCardMemberId,
      planName,
      setPlanName,
      planWellness,
      setPlanWellness,
      loginMID,
      setLoginMID,
      loginProxyId,
      setLoginProxyId,
      serviceNumber,
      setServiceNumber,
      setAuthToken,
      getAuthToken,
      setProxyMemberId,
      proxyMemberId,
      isMemberTabDefault,
      setIsMemberTabDefault,
      setSSOLink,
      ssoLink,
      isDesignatedTabShow,
      setIsDesignatedTabShow,
      clearContextValues,
      setProxyUniqueId,
      setProfileState,
      profileState,
      proxyUniqueId,
      xauthToken,
      setXauthToken,
      xsentaraTokenID,
      setXsentaraTokenID,
      onEmailUpdate,
      setOnEmailUpdate,
      ssoEndPoints,
      refetchSSOUrls,
      logging,
      setLogging,
      monitoring,
      setMonitoring,
      healthEquityHSA,
      setHealthEquityHSA,
      accountType,
      setAccountType,
      userTelecom,
      setUserTelecom,
    }),
    [
      addressValue,
      age,
      confirmProxy,
      designatedDropdownLink,
      enableDelete,
      enableView,
      getFeature,
      isDesignatedRepresentative,
      loginMemberId,
      memberProxyData,
      proxyData,
      proxyDataValue,
      proxyDropDown,
      proxySelected,
      proxyUpdate,
      showContent,
      updateProxy,
      userEncodedData,
      viewModal,
      zipValue,
      isGuestSwitch,
      setIsBacktoDashBoard,
      isBacktoDashBoard,
      primaryUser,
      setPrimaryUser,
      selectedIdCardMemberId,
      setSelectedIdCardMemberId,
      planName,
      setPlanName,
      planWellness,
      setPlanWellness,
      loginMID,
      setLoginMID,
      loginProxyId,
      setLoginProxyId,
      serviceNumber,
      setServiceNumber,
      setAuthToken,
      getAuthToken,
      setProxyMemberId,
      proxyMemberId,
      isMemberTabDefault,
      setIsMemberTabDefault,
      setSSOLink,
      ssoLink,
      isDesignatedTabShow,
      setIsDesignatedTabShow,
      clearContextValues,
      setProxyUniqueId,
      proxyUniqueId,
      setProfileState,
      profileState,
      xauthToken,
      setXauthToken,
      xsentaraTokenID,
      setXsentaraTokenID,
      onEmailUpdate,
      setOnEmailUpdate,
      ssoEndPoints,
      logging,
      setLogging,
      monitoring,
      setMonitoring,
      healthEquityHSA,
      setHealthEquityHSA,
      accountType,
      setAccountType,
      userTelecom,
    ]
  );
  return (
    <GlobalContext.Provider value={globalContextVal}>
      {/* Wrap children with FeatureToggleProvider context provider */}
      <FeatureToggleProvider>{children}</FeatureToggleProvider>
    </GlobalContext.Provider>
  );
};

export default GlobalProvider;
